import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import {
  EVENT_TIME_RANGES,
  eventTimeRangeLabel,
} from "@/features/events/constants/event-time-ranges";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import {
  EVENTS_CHART_LATENCY_METRICS,
  type EventsChartLatencyMetric,
} from "@/features/admin/lib/events-chart-metric";
import { SlowActionBudgetOverrides } from "../SlowActionBudgetOverrides";
import { LIMITS, RETENTION_DEFAULT_DAYS } from "./ops-settings-limits";
import { SlackWarning } from "./SlackWarning";
import type { OpsSettingsCardProps } from "./types";

/**
 * Named for what an operator is looking at rather than for the column: nobody
 * opens this page thinking in percentiles, they think "how long did they wait".
 */
function chartMetricLabel(metric: EventsChartLatencyMetric, t: TFunction): string {
  switch (metric) {
    case "endToEnd":
      return t("admin.opsSettings.chartMetricOption.endToEnd", "Waiting");
    case "run":
      return t("admin.opsSettings.chartMetricOption.run", "Working");
    case "both":
      return t("admin.opsSettings.chartMetricOption.both", "Waiting and working");
  }
}

/**
 * The events log: how long history is kept, what the tab opens showing, and
 * when a pile of failures is worth a Slack message. Zero retention doubles as
 * "off", so it is drawn as a switch with a dial under it rather than a number
 * field where 0 means something special.
 */
export function EventsSettingsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const retentionOn = draft.eventsRetentionDays > 0;

  return (
    <SettingsCard
      icon="history"
      title={t("admin.opsSettings.events.title", "Events log")}
      description={t(
        "admin.opsSettings.events.description",
        "The history of every background run. How long it is kept, what the tab opens showing, and when a pile of failures is worth a Slack message.",
      )}
    >
      <SettingRow
        htmlFor="events-retention"
        label={t("admin.opsSettings.retention", "Delete old events")}
        description={t(
          "admin.opsSettings.retentionHelp",
          "Off keeps every run forever. On, runs older than the window below are deleted for good — they cannot be brought back.",
        )}
        control={
          <SettingSwitch
            id="events-retention"
            checked={retentionOn}
            disabled={isReadOnly}
            onCheckedChange={(checked) =>
              set("eventsRetentionDays", checked ? RETENTION_DEFAULT_DAYS : 0)
            }
          />
        }
      />

      <SettingRow
        htmlFor="events-retention-days"
        isNested
        isMuted={!retentionOn}
        label={t("admin.opsSettings.retentionDays", "Keep events for")}
        description={t(
          "admin.opsSettings.retentionDaysHelp",
          "Old runs are cleaned up once an hour by the scheduled ops sweep, whether or not anybody is looking.",
        )}
        control={
          <NumberField
            id="events-retention-days"
            value={retentionOn ? draft.eventsRetentionDays : RETENTION_DEFAULT_DAYS}
            min={LIMITS.eventsRetentionDays.min}
            max={LIMITS.eventsRetentionDays.max}
            unit={t("admin.opsSettings.days", "days")}
            disabled={isReadOnly || !retentionOn}
            onChange={(value) => set("eventsRetentionDays", value)}
          />
        }
      />

      <SettingRow
        htmlFor="events-hide-pings"
        label={t("admin.opsSettings.hidePings", "Open with health pings hidden")}
        description={t(
          "admin.opsSettings.hidePingsHelp",
          "Pings dominate the log, so most visits want them out of the way. The toggle on the tab still shows them.",
        )}
        control={
          <SettingSwitch
            id="events-hide-pings"
            checked={draft.eventsHidePingsDefault}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("eventsHidePingsDefault", checked)}
          />
        }
      />

      <SettingRow
        label={t("admin.opsSettings.defaultWindow", "Open showing")}
        description={t(
          "admin.opsSettings.defaultWindowHelp",
          "The stretch of history the tab starts on. The range picker on the tab still changes it per visit.",
        )}
        control={
          <Select
            value={String(draft.eventsDefaultWindowHours)}
            disabled={isReadOnly}
            onValueChange={(value) => set("eventsDefaultWindowHours", Number(value))}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TIME_RANGES.map((range) => (
                <SelectItem key={range.value} value={String(range.hours)} className="text-xs">
                  {eventTimeRangeLabel(range.value, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="events-stuck-after"
        label={t("admin.opsSettings.stuckAfter", "Call a run stuck after")}
        description={t(
          "admin.opsSettings.stuckAfterHelp",
          "A run still processing past this is shown as timed out. Nothing is killed — the flag is how you notice.",
        )}
        control={
          <NumberField
            id="events-stuck-after"
            value={draft.eventsStuckAfterMinutes}
            min={LIMITS.eventsStuckAfterMinutes.min}
            max={LIMITS.eventsStuckAfterMinutes.max}
            unit={t("admin.opsSettings.minutes", "minutes")}
            disabled={isReadOnly}
            onChange={(value) => set("eventsStuckAfterMinutes", value)}
          />
        }
      />

      <SettingRow
        htmlFor="events-failure-alert"
        label={t("admin.opsSettings.failureAlert", "Post to Slack when failures pile up")}
        description={t(
          "admin.opsSettings.failureAlertHelp",
          "One message per bad stretch, to the admin-alerts Slack channel — not one message per failure.",
        )}
        control={
          <SettingSwitch
            id="events-failure-alert"
            checked={draft.alertOnFailedEvents}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnFailedEvents", checked)}
          />
        }
      />

      {draft.alertOnFailedEvents && !isSlackConfigured ? <SlackWarning /> : null}

      <SettingRow
        htmlFor="events-failure-threshold"
        isNested
        isMuted={!draft.alertOnFailedEvents}
        label={t("admin.opsSettings.failureThreshold", "Alert when at least")}
        description={t(
          "admin.opsSettings.failureThresholdHelp",
          "Counted over the last hour. Set it above your normal background noise, or the alert becomes the noise.",
        )}
        control={
          <NumberField
            id="events-failure-threshold"
            value={draft.failedEventsAlertThreshold}
            min={LIMITS.failedEventsAlertThreshold.min}
            max={LIMITS.failedEventsAlertThreshold.max}
            unit={t("admin.opsSettings.failuresPerHour", "failures/hour")}
            disabled={isReadOnly || !draft.alertOnFailedEvents}
            onChange={(value) => set("failedEventsAlertThreshold", value)}
          />
        }
      />

      <SettingRow
        htmlFor="events-slow-alert"
        label={t("admin.opsSettings.slowActionAlert", "Post to Slack when actions run long")}
        description={t(
          "admin.opsSettings.slowActionAlertHelp",
          "Measured end to end from the click, so queue time counts. Covers actions that finish late and actions that have not finished at all.",
        )}
        control={
          <SettingSwitch
            id="events-slow-alert"
            checked={draft.alertOnSlowActions}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnSlowActions", checked)}
          />
        }
      />

      {draft.alertOnSlowActions && !isSlackConfigured ? <SlackWarning /> : null}

      <SettingRow
        htmlFor="events-slow-default"
        isNested
        isMuted={!draft.alertOnSlowActions}
        label={t("admin.opsSettings.slowActionDefault", "Default end-to-end budget")}
        description={t(
          "admin.opsSettings.slowActionDefaultHelp",
          "Read the p95 on the Events tab before setting this. A budget below what healthy work already takes turns the alert into noise.",
        )}
        control={
          <NumberField
            id="events-slow-default"
            value={draft.slowActionSecondsDefault}
            min={LIMITS.slowActionSecondsDefault.min}
            max={LIMITS.slowActionSecondsDefault.max}
            unit={t("admin.opsSettings.seconds", "seconds")}
            disabled={isReadOnly || !draft.alertOnSlowActions}
            onChange={(value) => set("slowActionSecondsDefault", value)}
          />
        }
      />

      <SlowActionBudgetOverrides
        value={draft.slowActionSecondsByEventType}
        disabled={isReadOnly || !draft.alertOnSlowActions}
        onChange={(next) => set("slowActionSecondsByEventType", next)}
      />

      {/* The chart reads the budget whether or not the alert is switched on,
          so these are not nested under it: seeing the threshold is useful
          well before anyone wants to be paged about crossing it. */}
      <SettingRow
        label={t("admin.opsSettings.chartMetric", "Chart the time as")}
        description={t(
          "admin.opsSettings.chartMetricHelp",
          "Waiting is the whole trip from the click, including queue time. Working is only what the worker ran. Showing both is how a backed-up queue becomes visible — the gap between the lines is the wait.",
        )}
        control={
          <Select
            value={draft.eventsChartLatencyMetric}
            disabled={isReadOnly}
            onValueChange={(value) =>
              set("eventsChartLatencyMetric", value as EventsChartLatencyMetric)
            }
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENTS_CHART_LATENCY_METRICS.map((metric) => (
                <SelectItem key={metric} value={metric} className="text-xs">
                  {chartMetricLabel(metric, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="events-chart-budget-line"
        label={t("admin.opsSettings.chartBudgetLine", "Draw the budget on the chart")}
        description={t(
          "admin.opsSettings.chartBudgetLineHelp",
          "A dashed line at the default budget, so a slow stretch is visible without reading the axis. Hidden automatically when the budget sits far above everything in the window.",
        )}
        control={
          <SettingSwitch
            id="events-chart-budget-line"
            checked={draft.eventsChartShowBudgetLine}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("eventsChartShowBudgetLine", checked)}
          />
        }
      />
    </SettingsCard>
  );
}
